import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useRadixConnect, RadixConnectWithContext } from '../../../radix_connect';
import { useAuth, useGameData } from '../../../backend';
import { isSupabaseConfigured, supabase } from '../../../backend/supabase';
import Header from '../Header';
import Footer from '../Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import { FaImage, FaPalette, FaDownload, FaWallet, FaInfoCircle, FaTimes, FaChevronDown, FaChevronUp, FaGamepad } from 'react-icons/fa';
import './ImageCreator.scss';

const ImageCreator = () => {
  const { rdt, connected, accounts, selectedAccount } = useRadixConnect();
  const [isWalletConnected, setIsWalletConnected] = useState(connected);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [modalImage, setModalImage] = useState(null);
  const [isLoadingFromStorage, setIsLoadingFromStorage] = useState(true);
  
  // Customizable prompt inputs - Basic
  const [characterClothes, setCharacterClothes] = useState('a classy smoking');
  const [sceneLocation, setSceneLocation] = useState('in a trading floor with the chinese king CZ from Binance aside');
  const [characterAction, setCharacterAction] = useState('a hand on CZ\'s shoulder, smiling and joking at poor people that are not holding Reddicks yet');
  const [artStyle, setArtStyle] = useState('modern computer hand drawing');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  
  // Interactive Character Customization
  const [characterExpression, setCharacterExpression] = useState('-');
  const [characterPose, setCharacterPose] = useState('-');
  const [characterAccessories, setCharacterAccessories] = useState([]);
  const [characterSize, setCharacterSize] = useState('-');
  
  // Time & Weather Settings
  const [timeOfDay, setTimeOfDay] = useState('-');
  const [weatherCondition, setWeatherCondition] = useState('-');
  
  // Seasonal Themes
  const [seasonalTheme, setSeasonalTheme] = useState('-');
  
  // Character Interactions
  const [characterInteraction, setCharacterInteraction] = useState('-');
  
  // Celebrity/Cameo System
  const [celebrityCameo, setCelebrityCameo] = useState('-');
  
  // Color Palette Selector
  const [colorPalette, setColorPalette] = useState('-');
  
  // Lighting Effects
  const [lightingEffect, setLightingEffect] = useState('-');
  
  // Camera Angles
  const [cameraAngle, setCameraAngle] = useState('-');
  
  // Artistic Filters
  const [artisticFilter, setArtisticFilter] = useState('-');
  
  // UI State
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    characterCustomization: false,
    environment: false,
    interactions: false,
    visualEffects: false
  });
  
  // Art style options
  const artStyleOptions = [
    'modern computer hand drawing',
    'cartoon style',
    'pixel art',
    'watercolor painting',
    'oil painting',
    'anime/manga style',
    'comic book style',
    'minimalist line art'
  ];
  
  // Character expressions
  const expressionOptions = [
    { value: '-', label: '-' },
    { value: 'happy', label: 'Happy (toothy grin)' },
    { value: 'excited', label: 'Excited (wide eyes, big grin)' },
    { value: 'confused', label: 'Confused (tilted head, furrowed brows)' },
    { value: 'angry', label: 'Angry (frowning, intense eyes)' },
    { value: 'sad', label: 'Sad (droopy eyes, downturned mouth)' },
    { value: 'surprised', label: 'Surprised (wide eyes, mouth open)' },
    { value: 'cool', label: 'Cool (sunglasses, confident smirk)' },
    { value: 'silly', label: 'Silly (crossed eyes, tongue out)' }
  ];
  
  // Character poses
  const poseOptions = [
    { value: '-', label: '-' },
    { value: 'standing', label: 'Standing' },
    { value: 'sitting', label: 'Sitting' },
    { value: 'running', label: 'Running/Dashing' },
    { value: 'jumping', label: 'Jumping' },
    { value: 'dancing', label: 'Dancing' },
    { value: 'pointing', label: 'Pointing' },
    { value: 'waving', label: 'Waving' },
    { value: 'facepalm', label: 'Facepalm' },
    { value: 'shrugging', label: 'Shrugging' },
    { value: 'flexing', label: 'Flexing Muscles' }
  ];
  
  // Character accessories
  const accessoryOptions = [
    { value: 'sunglasses', label: 'Sunglasses' },
    { value: 'hat', label: 'Hat/Cap' },
    { value: 'crown', label: 'Crown' },
    { value: 'necklace', label: 'Necklace' },
    { value: 'watch', label: 'Watch' },
    { value: 'phone', label: 'Phone' },
    { value: 'laptop', label: 'Laptop' },
    { value: 'pizza_slice', label: 'Pizza Slice' },
    { value: 'crypto_coin', label: 'Crypto Coin' },
    { value: 'rocket', label: 'Rocket Toy' }
  ];
  
  // Time of day options
  const timeOfDayOptions = [
    { value: '-', label: '-' },
    { value: 'day', label: 'Day' },
    { value: 'night', label: 'Night' },
    { value: 'sunrise', label: 'Sunrise' },
    { value: 'sunset', label: 'Sunset' },
    { value: 'dusk', label: 'Dusk' },
    { value: 'dawn', label: 'Dawn' }
  ];
  
  // Weather conditions
  const weatherOptions = [
    { value: '-', label: '-' },
    { value: 'clear', label: 'Clear Sky' },
    { value: 'cloudy', label: 'Cloudy' },
    { value: 'rainy', label: 'Rainy' },
    { value: 'stormy', label: 'Stormy' },
    { value: 'snowy', label: 'Snowy' },
    { value: 'foggy', label: 'Foggy' },
    { value: 'windy', label: 'Windy' }
  ];
  
  // Seasonal themes
  const seasonalThemeOptions = [
    { value: '-', label: '-' },
    { value: 'spring', label: 'Spring (flowers, pastels)' },
    { value: 'summer', label: 'Summer (beach, bright colors)' },
    { value: 'autumn', label: 'Autumn (falling leaves, warm tones)' },
    { value: 'winter', label: 'Winter (snow, ice, cool tones)' },
    { value: 'christmas', label: 'Christmas (tree, decorations)' },
    { value: 'halloween', label: 'Halloween (pumpkins, spooky)' },
    { value: 'valentine', label: 'Valentine\'s Day (hearts, pink/red)' }
  ];
  
  // Character interactions
  const interactionOptions = [
    { value: '-', label: '-' },
    { value: 'multiple_reddicks', label: 'Multiple Reddicks Characters' },
    { value: 'friends', label: 'With Friends' },
    { value: 'enemy', label: 'With Enemy/Rival' },
    { value: 'mentor', label: 'With Mentor/Teacher' },
    { value: 'pet', label: 'With Pet' }
  ];
  
  // Celebrity cameo options
  const celebrityOptions = [
    { value: '-', label: '-' },
    { value: 'elon_musk', label: 'Elon Musk' },
    { value: 'cz_binance', label: 'CZ (Binance)' },
    { value: 'vitalik', label: 'Vitalik Buterin' },
    { value: 'satoshi', label: 'Satoshi Nakamoto' },
    { value: 'warren_buffett', label: 'Warren Buffett' },
    { value: 'jordan_belfort', label: 'Jordan Belfort' },
    { value: 'gordon_gekko', label: 'Gordon Gekko' }
  ];
  
  // Color palette options
  const colorPaletteOptions = [
    { value: '-', label: '-' },
    { value: 'vibrant', label: 'Vibrant (Bright, High Contrast)' },
    { value: 'pastel', label: 'Pastel (Soft, Light Colors)' },
    { value: 'monochrome', label: 'Monochrome (Black & White)' },
    { value: 'neon', label: 'Neon (Electric, Glowing)' },
    { value: 'warm', label: 'Warm (Reds, Oranges, Yellows)' },
    { value: 'cool', label: 'Cool (Blues, Greens, Purples)' },
    { value: 'retro', label: 'Retro (Vintage, Muted)' },
    { value: 'cyberpunk', label: 'Cyberpunk (Dark with Neon Accents)' }
  ];
  
  // Lighting effects
  const lightingOptions = [
    { value: '-', label: '-' },
    { value: 'natural', label: 'Natural Light' },
    { value: 'dramatic', label: 'Dramatic (High Contrast)' },
    { value: 'soft', label: 'Soft (Diffused, Gentle)' },
    { value: 'golden_hour', label: 'Golden Hour (Warm, Low Sun)' },
    { value: 'blue_hour', label: 'Blue Hour (Cool, Twilight)' },
    { value: 'neon', label: 'Neon (Artificial, Colorful)' },
    { value: 'spotlight', label: 'Spotlight (Focused Beam)' },
    { value: 'backlit', label: 'Backlit (Silhouette Effect)' }
  ];
  
  // Camera angles
  const cameraAngleOptions = [
    { value: '-', label: '-' },
    { value: 'eye_level', label: 'Eye Level' },
    { value: 'low_angle', label: 'Low Angle (Looking Up)' },
    { value: 'high_angle', label: 'High Angle (Looking Down)' },
    { value: 'bird_eye', label: 'Bird\'s Eye View (Top Down)' },
    { value: 'worm_eye', label: 'Worm\'s Eye View (Bottom Up)' },
    { value: 'dutch_angle', label: 'Dutch Angle (Tilted)' },
    { value: 'profile', label: 'Profile (Side View)' },
    { value: 'over_shoulder', label: 'Over Shoulder' }
  ];
  
  // Artistic filters
  const artisticFilterOptions = [
    { value: '-', label: '-' },
    { value: 'vintage', label: 'Vintage (Aged, Sepia Tone)' },
    { value: 'glitch', label: 'Glitch (Digital Distortion)' },
    { value: 'vaporwave', label: 'Vaporwave (80s Aesthetic)' },
    { value: 'noir', label: 'Film Noir (High Contrast B&W)' },
    { value: 'dreamy', label: 'Dreamy (Soft Focus, Hazy)' },
    { value: 'gritty', label: 'Gritty (Textured, Rough)' },
    { value: 'psychedelic', label: 'Psychedelic (Swirling Colors)' }
  ];

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle accessory selection
  const handleAccessoryToggle = (accessory) => {
    setCharacterAccessories(prev => {
      if (prev.includes(accessory)) {
        return prev.filter(a => a !== accessory);
      } else {
        return [...prev, accessory];
      }
    });
  };

  // Function to build the dynamic prompt
  const buildDynamicPrompt = () => {
    const additionalText = additionalInstructions.trim() ? ` ${additionalInstructions}` : '';
    
    // Build character description
    let characterDescription = `A red, thin capsule-shaped character with a kind toothy grin and happy eyes with silver round goggles`;
    
    // Add expression if not default
    if (characterExpression !== '-') {
      const expressionMap = {
        'happy': 'with a happy toothy grin',
        'excited': 'with excited wide eyes and a big grin',
        'confused': 'with a confused expression, tilted head and furrowed brows',
        'angry': 'with an angry frown and intense eyes',
        'sad': 'with sad droopy eyes and downturned mouth',
        'surprised': 'with surprised wide eyes and open mouth',
        'cool': 'with cool sunglasses and a confident smirk',
        'silly': 'with silly crossed eyes and tongue out'
      };
      
      characterDescription += ` ${expressionMap[characterExpression] || ''}`;
    }
    
    // Add pose if not default
    if (characterPose !== '-') {
      const poseMap = {
        'standing': 'standing',
        'sitting': 'sitting',
        'running': 'running/dashing',
        'jumping': 'jumping',
        'dancing': 'dancing',
        'pointing': 'pointing',
        'waving': 'waving',
        'facepalm': 'doing a facepalm',
        'shrugging': 'shrugging',
        'flexing': 'flexing muscles'
      };
      
      characterDescription += ` ${poseMap[characterPose] || ''}`;
    }
    
    // Add size if not default
    if (characterSize !== '-') {
      const sizeMap = {
        'tiny': 'tiny (very small)',
        'small': 'small',
        'normal': 'normal size',
        'large': 'large',
        'giant': 'giant (very big)'
      };
      
      characterDescription += ` ${sizeMap[characterSize] || ''}`;
    }
    
    // Add accessories
    if (characterAccessories.length > 0) {
      const accessoryMap = {
        'sunglasses': 'wearing cool sunglasses',
        'hat': 'wearing a hat',
        'crown': 'wearing a crown',
        'necklace': 'wearing a necklace',
        'watch': 'wearing a watch',
        'phone': 'holding a phone',
        'laptop': 'using a laptop',
        'pizza_slice': 'holding a pizza slice',
        'crypto_coin': 'holding a crypto coin',
        'rocket': 'holding a rocket toy'
      };
      
      const accessoryDescriptions = characterAccessories.map(a => accessoryMap[a] || '').filter(Boolean);
      if (accessoryDescriptions.length > 0) {
        characterDescription += `, ${accessoryDescriptions.join(', ')}`;
      }
    }
    
    // Build environment description
    let environmentDescription = sceneLocation;
    
    // Add time of day if not default
    if (timeOfDay !== '-') {
      const timeMap = {
        'day': 'during the day',
        'night': 'at night',
        'sunrise': 'at sunrise',
        'sunset': 'at sunset',
        'dusk': 'at dusk',
        'dawn': 'at dawn'
      };
      
      environmentDescription += ` ${timeMap[timeOfDay] || ''}`;
    }
    
    // Add weather if not default
    if (weatherCondition !== '-') {
      const weatherMap = {
        'clear': 'with clear sky',
        'cloudy': 'with cloudy sky',
        'rainy': 'in the rain',
        'stormy': 'during a storm',
        'snowy': 'in the snow',
        'foggy': 'in foggy conditions',
        'windy': 'in windy conditions'
      };
      
      environmentDescription += ` ${weatherMap[weatherCondition] || ''}`;
    }
    
    // Add seasonal theme if not default
    if (seasonalTheme !== '-') {
      const seasonMap = {
        'spring': 'with spring flowers and pastel colors',
        'summer': 'with summer beach vibes and bright colors',
        'autumn': 'with autumn falling leaves and warm tones',
        'winter': 'with winter snow and ice',
        'christmas': 'with Christmas decorations and festive atmosphere',
        'halloween': 'with Halloween pumpkins and spooky atmosphere',
        'valentine': 'with Valentine\'s hearts and pink/red colors'
      };
      
      environmentDescription += ` ${seasonMap[seasonalTheme] || ''}`;
    }
    
    // Add character interactions if not default
    let interactionDescription = '';
    if (characterInteraction !== '-') {
      const interactionMap = {
        'multiple_reddicks': 'with other Reddicks characters',
        'friends': 'with friends',
        'enemy': 'with an enemy/rival',
        'mentor': 'with a mentor/teacher',
        'pet': 'with a pet'
      };
      
      interactionDescription = ` ${interactionMap[characterInteraction] || ''}`;
    }
    
    // Add celebrity cameo if not default
    let celebrityDescription = '';
    if (celebrityCameo !== '-') {
      const celebrityMap = {
        'elon_musk': 'with Elon Musk',
        'cz_binance': 'with CZ from Binance',
        'vitalik': 'with Vitalik Buterin',
        'satoshi': 'with Satoshi Nakamoto',
        'warren_buffett': 'with Warren Buffett',
        'jordan_belfort': 'with Jordan Belfort',
        'gordon_gekko': 'with Gordon Gekko'
      };
      
      celebrityDescription = ` ${celebrityMap[celebrityCameo] || ''}`;
    }
    
    // Add visual effects
    let visualEffectsDescription = '';
    
    // Color palette if not default
    if (colorPalette !== '-') {
      const colorMap = {
        'vibrant': 'vibrant bright colors with high contrast',
        'pastel': 'soft pastel colors',
        'monochrome': 'monochrome black and white',
        'neon': 'electric neon glowing colors',
        'warm': 'warm colors (reds, oranges, yellows)',
        'cool': 'cool colors (blues, greens, purples)',
        'retro': 'vintage muted retro colors',
        'cyberpunk': 'dark colors with neon accents'
      };
      
      visualEffectsDescription += ` ${colorMap[colorPalette] || ''}`;
    }
    
    // Lighting if not default
    if (lightingEffect !== '-') {
      const lightingMap = {
        'natural': 'with natural lighting',
        'dramatic': 'with dramatic high contrast lighting',
        'soft': 'with soft diffused lighting',
        'golden_hour': 'with golden hour warm lighting',
        'blue_hour': 'with blue hour cool twilight lighting',
        'neon': 'with neon artificial colorful lighting',
        'spotlight': 'with focused spotlight lighting',
        'backlit': 'with backlit silhouette lighting'
      };
      
      visualEffectsDescription += ` ${lightingMap[lightingEffect] || ''}`;
    }
    
    // Camera angle if not default
    if (cameraAngle !== '-') {
      const cameraMap = {
        'eye_level': 'from eye level angle',
        'low_angle': 'from low angle looking up',
        'high_angle': 'from high angle looking down',
        'bird_eye': 'from bird\'s eye view top down',
        'worm_eye': 'from worm\'s eye view bottom up',
        'dutch_angle': 'from dutch tilted angle',
        'profile': 'from profile side view',
        'over_shoulder': 'from over shoulder view'
      };
      
      visualEffectsDescription += ` ${cameraMap[cameraAngle] || ''}`;
    }
    
    // Artistic filter if not default
    if (artisticFilter !== '-') {
      const filterMap = {
        'vintage': 'with vintage aged sepia tone effect',
        'glitch': 'with digital glitch distortion effect',
        'vaporwave': 'with 80s vaporwave aesthetic',
        'noir': 'with film noir high contrast black and white',
        'dreamy': 'with dreamy soft focus hazy effect',
        'gritty': 'with gritty textured rough effect',
        'psychedelic': 'with psychedelic swirling colors'
      };
      
      visualEffectsDescription += ` ${filterMap[artisticFilter] || ''}`;
    }
    
    return `Context: these are memes about REDDICKS, a crypto meme coin, and the characters are called reddicks. The meme coin is on the Radix network. Special emphasizing on the fact that we are making crypto memes so it can be degenerate, crazy, weird.
Image to generate: ${characterDescription}. This is important that the character is exactly as described just before as he is the mascot. The character is wearing ${characterClothes}. The scene takes place in the ${environmentDescription}, where the character is ${characterAction}${interactionDescription}${celebrityDescription}. The art style is like a ${artStyle}, not too clean with imperfections. Don't hesitate to create a whole new environment and if the user is for example asking about making a character on a special environment, don't hesitate to use references of actual environment. Also, don't hesitate to, if nothing is specified or if it could be great for a meme, use actual bodies and clothings from existant real characters and simply insert the red head and hands over it, you can even make it slightly disproportionate. We are also promoting the fact that the token is BULLISH so don't hesitate to add green candles, thin ones, long ones, thic ones (depending on the whole meme context), a lot, but in intelligent, smart places, like you won't have some simply floating in a wall, make them interesting. Add a texts/sign/mark "REDDICKS" "$REDDICKS", intelligently, but be careful because you are still limited and are still making mistakes when typing words inside the image - so make sure it is accurate, not missing a character and if not sure, just add the main one. Make sure it is funny, add small miscs that will make the difference and be creative, to make sure it looks like it has not been made by AI but by an amateur hand drawer. The image is full colored with${visualEffectsDescription}. Here are a few more instructions from the user, please follow them carefully and as long as they go with the overall Reddicks meme thing, but try to incorporate them nicely:${additionalText}`;
  };

  // Check wallet connection status
  useEffect(() => {
    setIsWalletConnected(connected);
  }, [connected]);

  // Derive wallet state from accounts
  const wallet = {
    rdt,
    connected: isWalletConnected,
    address: selectedAccount?.address || (accounts && accounts.length > 0 ? accounts[0].address : null),
    accounts: accounts
  };

  // Use auth hook to get user data
  const { 
    userId, 
    isAuthenticated,
    sessionToken,
  } = useAuth(isWalletConnected ? wallet : { connected: false, address: null });

  // Use game data hook to get user profile data
  const {
    gameSave,
    loadSave,
  } = useGameData(isAuthenticated ? userId : null, sessionToken);

  // Load user profile and check for Reddicks holder status
  useEffect(() => {
    if (isAuthenticated && userId && gameSave) {
      const hasRedicksValue = gameSave.has_redicks === true || gameSave.data?.has_redicks === true;
      const generationsValue = typeof gameSave.generations === 'number' ? gameSave.generations : (typeof gameSave.data?.generations === 'number' ? gameSave.data.generations : 0);
      
      setUserProfile({
        userId: userId,
        walletAddress: wallet.address,
        companyName: gameSave.company_name || 'Anonymous User',
        createdAt: gameSave.created_at,
        lastPlayed: gameSave.last_played,
        money: gameSave.money || 0,
        miningPower: gameSave.mining_power || 0,
        totalImagesGenerated: gameSave.data?.totalImagesGenerated || 0,
        has_redicks: hasRedicksValue,
        generations: generationsValue,
      });
    } else {
        // Reset if user disconnects or data is not available
        setUserProfile(null);
    }
  }, [isAuthenticated, userId, gameSave, wallet.address]);

  // Function to save images to localStorage with rolling 5-image system
  const saveImagesToStorage = (images) => {
    try {
      // Handle empty array case - clear storage completely
      if (images.length === 0) {
        console.log('Clearing all images from storage');
        localStorage.removeItem('reddicks_generated_images');
        sessionStorage.removeItem('reddicks_generated_images');
        return;
      }
      
      // Keep only the last 5 images (rolling system)
      const maxImages = 5;
      const rollingImages = images.slice(0, maxImages);
      
      const imagesData = JSON.stringify(rollingImages);
      console.log('Saving images to localStorage:', rollingImages.length, 'images (rolling system)');
      console.log('Data size:', imagesData.length, 'characters');
      
      localStorage.setItem('reddicks_generated_images', imagesData);
      
      // Verify the data was saved
      const savedData = localStorage.getItem('reddicks_generated_images');
      console.log('Verification - saved data length:', savedData ? savedData.length : 0);
      
      // Show notification if an image was removed
      if (images.length > maxImages) {
        console.log(`Removed ${images.length - maxImages} oldest image(s) to maintain 5-image limit`);
      }
    } catch (error) {
      console.error('Error saving images to localStorage:', error);
      // Fallback to sessionStorage if localStorage is full
      try {
        const imagesData = JSON.stringify(images.slice(0, 5));
        sessionStorage.setItem('reddicks_generated_images', imagesData);
        console.log('Fallback: saved to sessionStorage');
      } catch (sessionError) {
        console.error('Error saving to sessionStorage:', sessionError);
      }
    }
  };

  // Function to load images from localStorage
  const loadImagesFromStorage = () => {
    try {
      console.log('Loading images from localStorage...');
      const imagesData = localStorage.getItem('reddicks_generated_images');
      
      if (imagesData) {
        console.log('Found localStorage data, length:', imagesData.length);
        const parsedImages = JSON.parse(imagesData);
        console.log('Parsed images:', parsedImages.length, 'images');
        return parsedImages;
      } else {
        // Try sessionStorage as fallback
        const sessionData = sessionStorage.getItem('reddicks_generated_images');
        if (sessionData) {
          console.log('Found sessionStorage data, length:', sessionData.length);
          const parsedImages = JSON.parse(sessionData);
          console.log('Parsed images from sessionStorage:', parsedImages.length, 'images');
          return parsedImages;
        }
      }
      
      console.log('No saved images found');
      return [];
    } catch (error) {
      console.error('Error loading images from storage:', error);
      return [];
    }
  };

  // Load images from storage on component mount
  useEffect(() => {
    console.log('Component mounted, loading images from storage...');
    const savedImages = loadImagesFromStorage();
    if (savedImages.length > 0) {
      console.log('Setting loaded images:', savedImages.length, 'images');
      setGeneratedImages(savedImages);
    } else {
      console.log('No saved images found');
    }
    // Set loading flag to false after loading is complete
    setIsLoadingFromStorage(false);
  }, []);

  // Add a separate effect to save images whenever they change (but not on initial load)
  useEffect(() => {
    // Save if this isn't the initial load from storage (including empty arrays)
    if (!isLoadingFromStorage) {
      console.log('Images state changed, saving to storage...');
      saveImagesToStorage(generatedImages);
    }
  }, [generatedImages, isLoadingFromStorage]);

  // Function to refresh user data from backend
  const refreshUserData = async () => {
    if (!isAuthenticated || !userId) return;
    
    try {
      const data = await loadSave({ silent: true });

      // Manually update the userProfile state with the latest data
      if (data) {
        const hasRedicksValue = data.has_redicks === true || data.data?.has_redicks === true;
        const generationsValue = typeof data.generations === 'number' ? data.generations : (typeof data.data?.generations === 'number' ? data.data.generations : 0);
        
        setUserProfile(prev => ({
          ...prev,
          userId: userId,
          walletAddress: wallet.address,
          companyName: data.company_name || 'Anonymous User',
          createdAt: data.created_at,
          lastPlayed: data.last_played,
          money: data.money || 0,
          miningPower: data.mining_power || 0,
          totalImagesGenerated: data.data?.totalImagesGenerated || 0,
          has_redicks: hasRedicksValue,
          generations: generationsValue,
        }));
      }
    } catch (err) {
      console.error('Unexpected error refreshing user data:', err);
    }
  };

  // Handle image generation with proper validation
  const handleGenerateImage = async (prompt) => {
    // Only check if user is authenticated - let the server validate everything else
    if (!isAuthenticated) {
      setError('Please connect your wallet to generate images');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const imageUrl = await generateImageWithOpenRouter(prompt);

      if (imageUrl) {
        const newImage = {
          id: Date.now(),
          prompt: prompt,
          url: imageUrl,
          createdAt: new Date().toISOString(),
          creator: wallet.address
        };

        setGeneratedImages(prev => {
          const updatedImages = [newImage, ...prev];
          // Apply rolling 5-image limit
          const finalImages = updatedImages.slice(0, 5);

          // Show notification if an image was removed
          if (updatedImages.length > 5) {
            console.log('Image limit reached: Removed oldest image to make room for new one');
          }

          // Note: Saving is now handled by the useEffect hook
          return finalImages;
        });

        // Show the image in modal
        setModalImage(newImage);
      } else {
        setError('Failed to generate image. Please try again.');
        return;
      }

      // Refresh user data from database to get updated generation count
      await refreshUserData();

    } catch (err) {
      console.error('Unexpected generation error:', err);

      // Handle different types of errors
      if (err.message?.includes('network')) {
        setError('Network error. Please check your connection and try again.');
      } else if (err.message?.includes('timeout')) {
        setError('Request timed out. Please try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to call serverless API for image generation
  const generateImageWithOpenRouter = async (prompt) => {
    try {
      if (!supabase || !isSupabaseConfigured) {
        setError('Image generation service is not configured.');
        return null;
      }

      // Simple preprompt for Reddicks image generation
      const preprompt = "You are processing a user's order for image generation. Only generate images that are about Reddicks (red capsule-shaped characters with goggles). If the request is completely outside this scope, do not process the image. Generate only the image with no text or explanations.";
      const dynamicPrompt = buildDynamicPrompt();
      const fullPrompt = `${preprompt}\n\n${dynamicPrompt}`;

      // Call our Supabase Edge Function instead of OpenRouter directly
      const { data: functionData, error: functionError } = await supabase.functions.invoke('generate-image', {
        body: { prompt: fullPrompt },
        headers: { 'x-radcoon-session': sessionToken }
      });
      
      if (functionError) {
        let functionErrorDetail = '';
        try {
          if (typeof functionError.context?.json === 'function') {
            const errorBody = await functionError.context.json();
            functionErrorDetail = errorBody?.error || errorBody?.message || '';
          }
        } catch (error) {
          console.warn('Could not parse generate-image error body:', error);
        }

        const errorMsg = functionErrorDetail || `API Error: ${functionError.message}`;
        console.error('Supabase Edge Function error:', errorMsg);
        setError(errorMsg);
        return null;
      }
      
      // Convert the response to match what the rest of the code expects
      const response = { ok: true, json: async () => ({ ...functionData }) };

      if (!response.ok) {
        const errorMsg = `API Error: ${response.status} ${response.statusText}`;
        console.error('Serverless API error:', errorMsg);
        
        // Try to get more detailed error from response
        let errorDetails = '';
        try {
          const errorData = await response.json();
          errorDetails = errorData.error || '';
        } catch (e) {
          // If we can't parse the error response, use status text
          errorDetails = response.statusText;
        }
        
        console.error('Error details:', errorDetails);
        setError(errorMsg + (errorDetails ? `: ${errorDetails}` : ''));
        return null;
      }

      const responseData = await response.json();
      console.log('Serverless API response:', responseData);
      
      // Handle Google Gemini response format through OpenRouter (same as before)
      if (responseData.candidates && responseData.candidates[0] && responseData.candidates[0].content) {
        const content = responseData.candidates[0].content;
        console.log('Content structure:', JSON.stringify(content, null, 2));
        
        // Check if content has parts array (Google Gemini format)
        if (content.parts && Array.isArray(content.parts)) {
          console.log('Found parts array:', content.parts);
          for (const part of content.parts) {
            console.log('Part:', part);
            // Check for inlineData (base64 image)
            if (part.inlineData && part.inlineData.data) {
              console.log('Found inlineData image');
              const imageData = part.inlineData.data;
              // Convert base64 to data URL
              return `data:image/png;base64,${imageData}`;
            }
            // Check for text response (model returned text instead of image)
            if (part.text) {
              console.log('Model returned text instead of image:', part.text);
              const errorMsg = `The model returned text instead of an image. This might happen if the prompt is unclear or the model cannot generate an image for this request. Try rephrasing your prompt.`;
              setError(errorMsg);
              return null;
            }
          }
        }
        
        // Fallback to direct content if no parts
        if (content.text) {
          const textContent = content.text;
          console.log('Direct text content:', textContent);
          
          // Check if it's a URL
          if (textContent.startsWith('http')) {
            return textContent;
          }
          
          // Check if it's base64 image data
          if (textContent.startsWith('data:image')) {
            return textContent;
          }
          
          // Check if content contains a URL in markdown format
          const urlMatch = textContent.match(/!\[.*?\]\((https?:\/\/[^\s)]+)\)/);
          if (urlMatch && urlMatch[1]) {
            return urlMatch[1];
          }
          
          // Check if content is just a plain URL
          const plainUrlMatch = textContent.match(/(https?:\/\/[^\s]+)/);
          if (plainUrlMatch && plainUrlMatch[1]) {
            return plainUrlMatch[1];
          }
          
          // Model returned text instead of image
          console.error('Model returned text instead of image:', textContent);
          const errorMsg = `The model returned text instead of an image: "${textContent.substring(0, 100)}${textContent.length > 100 ? '...' : ''}". Try being more specific about the image you want.`;
          setError(errorMsg);
          return null;
        }
      }
      
      // Fallback to OpenAI-style format
      if (responseData.choices && responseData.choices[0] && responseData.choices[0].message) {
        const message = responseData.choices[0].message;
        console.log('OpenAI-style message:', message);
        
        // Check for IMAGE_SAFETY finish reason
        if (responseData.choices[0].finish_reason === 'stop' && responseData.choices[0].native_finish_reason === 'IMAGE_SAFETY') {
          const errorMsg = 'The image was blocked due to safety concerns. Please try a different description or modify your request.';
          console.error('IMAGE_SAFETY error:', errorMsg);
          setError(errorMsg);
          return null;
        }
        
        // Check for images array (OpenRouter format for image generation)
        if (message.images && Array.isArray(message.images) && message.images.length > 0) {
          for (const imageObj of message.images) {
            if (imageObj.type === 'image_url' && imageObj.image_url && imageObj.image_url.url) {
              const imageUrl = imageObj.image_url.url;
              console.log('Found image URL:', imageUrl);
              
              // Check if it's already a data URL
              if (imageUrl.startsWith('data:image')) {
                return imageUrl;
              }
              
              // Check if it's a regular URL
              if (imageUrl.startsWith('http')) {
                return imageUrl;
              }
            }
          }
        }
        
        if (message.content) {
          const content = message.content;
          
          // Check if it's a URL
          if (content.startsWith('http')) {
            return content;
          }
          
          // Check if it's base64 image data
          if (content.startsWith('data:image')) {
            return content;
          }
          
          // Model returned text instead of image
          console.error('Model returned text instead of image:', content);
          const errorMsg = `The model returned text instead of an image: "${content.substring(0, 100)}${content.length > 100 ? '...' : ''}". Try being more specific about the image you want.`;
          setError(errorMsg);
          return null;
        }
      }

      const errorMsg = 'No image data received from the model. The response format was unexpected.';
      console.error(errorMsg);
      setError(errorMsg);
      return null;
    } catch (error) {
      const errorMsg = `Network error: ${error.message}. Please check your connection and try again.`;
      console.error('Error calling serverless API:', error);
      setError(errorMsg);
      return null;
    }
  };

  // Handle opening image in modal
  const handleOpenImageModal = (image) => {
    setModalImage(image);
  };

  // Handle closing modal
  const handleCloseModal = () => {
    setModalImage(null);
  };

  // Handle image download
  const handleDownloadImage = (imageUrl, prompt) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `reddicks-image-${prompt.replace(/\s+/g, '-').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle delete image
  const handleDeleteImage = (imageId) => {
    setGeneratedImages(prev => {
      const updatedImages = prev.filter(img => img.id !== imageId);
      // Note: Saving is now handled by the useEffect hook
      return updatedImages;
    });
  };

  // Handle clear all images
  const handleClearAllImages = () => {
    if (window.confirm('Are you sure you want to delete all your generated images?')) {
      setGeneratedImages([]);
      // Note: Saving is now handled by the useEffect hook
    }
  };

  return (
    <div className="image-creator-page" data-scroll-root>
      <Header />
      
      <main className="image-creator-content">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="container">
            <div className="hero-content">
              <h1 className="page-title">
                <FaPalette /> Reddicks Image Creator
              </h1>
              <p className="page-subtitle">
                Create amazing AI-generated images powered by the Radix network
              </p>
            </div>
          </div>
        </section>

        {/* Wallet Connect and Info Boxes - Between Hero and Generator */}
        <div className="wallet-connect-container">
          {/* Wallet Connect Box */}
          <div className="wallet-connect-box">
            <div className="wallet-connect-header">
              <FaWallet className="wallet-icon" />
              <h3>Connect Your Wallet</h3>
            </div>
            <div className="wallet-connect-button-container">
              <RadixConnectWithContext />
            </div>
            
            {!isAuthenticated ? (
              <div className="wallet-connect-info">
                <FaInfoCircle className="info-icon" />
                <p>Please connect your wallet to be able to generate AI images about Reddicks.</p>
              </div>
            ) : (
              <div className="wallet-connect-info">
                <FaInfoCircle className="info-icon" />
                {userProfile?.has_redicks ? (
                 <div className="user-generations">
                   <p>Welcome, {userProfile?.companyName || 'User'}!</p>
                   <div className="generations-info">
                     <p>You have <span className="generations-count">{userProfile.generations}</span> generations left today. They reset at midnight UTC.</p>
                   </div>
                 </div>
                ) : (
                  <p>You must hold at least 15,000 Reddicks in your connected wallet to be able to generate images.</p>
                )}
              </div>
            )}
          </div>

          {/* Generator Info Box */}
          <div className="generator-info-box">
            <div className="info-header">
              <FaInfoCircle className="info-icon" />
              <h3>Generator Requirements</h3>
            </div>
            <div className="info-content">
              <div className="info-section">
                <h4>🎯 Requirements</h4>
                <div className="requirement-item">
                  <span className="requirement-icon">💰</span>
                  <span className="requirement-text">Must hold 15,000 Reddicks in your connected wallet</span>
                </div>
                <div className="requirement-item">
                  <span className="requirement-icon">🎮</span>
                  <span className="requirement-text">Wallet must be connected on the game</span>
                </div>
                <div className="requirement-item">
                  <span className="requirement-icon">🏢</span>
                  <span className="requirement-text">Must have a created company in the game</span>
                </div>
                <div className="requirement-item">
                  <span className="requirement-icon">⚡</span>
                  <span className="requirement-text">Limited generations per day (resets at midnight UTC)</span>
                </div>
              </div>
              
              <div className="info-section">
                <h4>🚀 How It Works</h4>
                <p>1. Connect your Radix wallet to the system</p>
                <p>2. Ensure you hold 15,000+ Reddicks tokens</p>
                <p>3. Create your company in the game</p>
                <p>4. Customize your Reddicks character with various options</p>
                <p>5. Generate unique AI-powered memes and images</p>
                <p>6. Download and share your creations!</p>
              </div>
              
              <div className="info-section">
                <h4>🎮 Get Started</h4>
                <p>New to Reddicks? Start your journey in the game!</p>
                <Link to="/game-info" className="game-button">
                  <FaGamepad /> Play Game
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Image Generator Section */}
        <section className="generator-section">
          <div className="container">
            <div className="generator-card">
              <h2 className="section-title text-left">
                <FaImage /> Create Your Image
              </h2>
              
              {!isAuthenticated ? (
                <div className="auth-prompt">
                  <p>Please connect your wallet to start creating images</p>
                </div>
              ) : !userProfile?.has_redicks ? (
                <div className="auth-prompt">
                  <p>You need to hold at least 15,000 Reddicks to generate images</p>
                </div>
              ) : (
                <div className="generator-form">
                  {/* Basic Customization Section */}
                  <div className="customization-section">
                    <div className="section-header" onClick={() => toggleSection('basic')}>
                      <h3 className="customization-title">Basic Customization</h3>
                      {expandedSections.basic ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                    
                    {expandedSections.basic && (
                      <div className="prompt-customization">
                        <div className="input-row">
                          <div className="input-group">
                            <label htmlFor="clothes">Character Clothes</label>
                            <input
                              id="clothes"
                              type="text"
                              placeholder="e.g., hobbo clothes, business suit, superhero costume"
                              value={characterClothes}
                              onChange={(e) => setCharacterClothes(e.target.value)}
                              disabled={loading}
                              maxLength="100"
                            />
                          </div>
                          
                          <div className="input-group">
                            <label htmlFor="location">Scene Location</label>
                            <input
                              id="location"
                              type="text"
                              placeholder="e.g., world trade center, moon surface, cyberpunk city"
                              value={sceneLocation}
                              onChange={(e) => setSceneLocation(e.target.value)}
                              disabled={loading}
                              maxLength="100"
                            />
                          </div>
                        </div>
                        
                        <div className="input-row">
                          <div className="input-group">
                            <label htmlFor="action">Character Action</label>
                            <textarea
                              id="action"
                              placeholder="e.g., looking at his richer peers, dancing on a rainbow, building a rocket"
                              rows="2"
                              value={characterAction}
                              onChange={(e) => setCharacterAction(e.target.value)}
                              disabled={loading}
                              maxLength="150"
                            />
                          </div>
                          
                          <div className="input-group">
                            <label htmlFor="art-style">Art Style</label>
                            <select
                              id="art-style"
                              value={artStyle}
                              onChange={(e) => setArtStyle(e.target.value)}
                              disabled={loading}
                            >
                              {artStyleOptions.map((style) => (
                                <option key={style} value={style}>
                                  {style}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        <div className="input-row">
                          <div className="input-group full-width">
                            <label htmlFor="additional-instructions">Additional Instructions (Optional)</label>
                            <textarea
                              id="additional-instructions"
                              placeholder="e.g., add flying pizza slices, make it raining money, include a rainbow in the background"
                              rows="2"
                              value={additionalInstructions}
                              onChange={(e) => setAdditionalInstructions(e.target.value)}
                              disabled={loading}
                              maxLength="200"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Interactive Character Customization Section */}
                  <div className="customization-section">
                    <div className="section-header" onClick={() => toggleSection('characterCustomization')}>
                      <h3 className="customization-title">Interactive Character Customization</h3>
                      {expandedSections.characterCustomization ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                    
                    {expandedSections.characterCustomization && (
                      <div className="prompt-customization">
                        <div className="input-row">
                          <div className="input-group">
                            <label htmlFor="expression">Character Expression</label>
                            <select
                              id="expression"
                              value={characterExpression}
                              onChange={(e) => setCharacterExpression(e.target.value)}
                              disabled={loading}
                            >
                              {expressionOptions.map((expression) => (
                                <option key={expression.value} value={expression.value}>
                                  {expression.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="input-group">
                            <label htmlFor="pose">Character Pose</label>
                            <select
                              id="pose"
                              value={characterPose}
                              onChange={(e) => setCharacterPose(e.target.value)}
                              disabled={loading}
                            >
                              {poseOptions.map((pose) => (
                                <option key={pose.value} value={pose.value}>
                                  {pose.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        <div className="input-row">
                          <div className="input-group">
                            <label htmlFor="size">Character Size</label>
                            <select
                              id="size"
                              value={characterSize}
                              onChange={(e) => setCharacterSize(e.target.value)}
                              disabled={loading}
                            >
                              <option value="-">-</option>
                              <option value="tiny">Tiny</option>
                              <option value="small">Small</option>
                              <option value="normal">Normal</option>
                              <option value="large">Large</option>
                              <option value="giant">Giant</option>
                            </select>
                          </div>
                          
                          <div className="input-group">
                            <label>Accessories</label>
                            <div className="accessories-grid">
                              {accessoryOptions.map((accessory) => (
                                <div key={accessory.value} className="accessory-item">
                                  <input
                                    type="checkbox"
                                    id={accessory.value}
                                    checked={characterAccessories.includes(accessory.value)}
                                    onChange={() => handleAccessoryToggle(accessory.value)}
                                    disabled={loading}
                                  />
                                  <label htmlFor={accessory.value}>{accessory.label}</label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Environment Section */}
                  <div className="customization-section">
                    <div className="section-header" onClick={() => toggleSection('environment')}>
                      <h3 className="customization-title">Time, Weather & Seasonal Settings</h3>
                      {expandedSections.environment ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                    
                    {expandedSections.environment && (
                      <div className="prompt-customization">
                        <div className="input-row">
                          <div className="input-group">
                            <label htmlFor="time-of-day">Time of Day</label>
                            <select
                              id="time-of-day"
                              value={timeOfDay}
                              onChange={(e) => setTimeOfDay(e.target.value)}
                              disabled={loading}
                            >
                              {timeOfDayOptions.map((time) => (
                                <option key={time.value} value={time.value}>
                                  {time.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="input-group">
                            <label htmlFor="weather">Weather Condition</label>
                            <select
                              id="weather"
                              value={weatherCondition}
                              onChange={(e) => setWeatherCondition(e.target.value)}
                              disabled={loading}
                            >
                              {weatherOptions.map((weather) => (
                                <option key={weather.value} value={weather.value}>
                                  {weather.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        <div className="input-row">
                          <div className="input-group">
                            <label htmlFor="seasonal-theme">Seasonal Theme</label>
                            <select
                              id="seasonal-theme"
                              value={seasonalTheme}
                              onChange={(e) => setSeasonalTheme(e.target.value)}
                              disabled={loading}
                            >
                              {seasonalThemeOptions.map((theme) => (
                                <option key={theme.value} value={theme.value}>
                                  {theme.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Interactions Section */}
                  <div className="customization-section">
                    <div className="section-header" onClick={() => toggleSection('interactions')}>
                      <h3 className="customization-title">Character Interactions & Celebrity Cameos</h3>
                      {expandedSections.interactions ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                    
                    {expandedSections.interactions && (
                      <div className="prompt-customization">
                        <div className="input-row">
                          <div className="input-group">
                            <label htmlFor="character-interaction">Character Interaction</label>
                            <select
                              id="character-interaction"
                              value={characterInteraction}
                              onChange={(e) => setCharacterInteraction(e.target.value)}
                              disabled={loading}
                            >
                              {interactionOptions.map((interaction) => (
                                <option key={interaction.value} value={interaction.value}>
                                  {interaction.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="input-group">
                            <label htmlFor="celebrity-cameo">Celebrity Cameo</label>
                            <select
                              id="celebrity-cameo"
                              value={celebrityCameo}
                              onChange={(e) => setCelebrityCameo(e.target.value)}
                              disabled={loading}
                            >
                              {celebrityOptions.map((celebrity) => (
                                <option key={celebrity.value} value={celebrity.value}>
                                  {celebrity.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Visual Effects Section */}
                  <div className="customization-section">
                    <div className="section-header" onClick={() => toggleSection('visualEffects')}>
                      <h3 className="customization-title">Visual Effects & Styling</h3>
                      {expandedSections.visualEffects ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                    
                    {expandedSections.visualEffects && (
                      <div className="prompt-customization">
                        <div className="input-row">
                          <div className="input-group">
                            <label htmlFor="color-palette">Color Palette</label>
                            <select
                              id="color-palette"
                              value={colorPalette}
                              onChange={(e) => setColorPalette(e.target.value)}
                              disabled={loading}
                            >
                              {colorPaletteOptions.map((palette) => (
                                <option key={palette.value} value={palette.value}>
                                  {palette.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="input-group">
                            <label htmlFor="lighting">Lighting Effect</label>
                            <select
                              id="lighting"
                              value={lightingEffect}
                              onChange={(e) => setLightingEffect(e.target.value)}
                              disabled={loading}
                            >
                              {lightingOptions.map((lighting) => (
                                <option key={lighting.value} value={lighting.value}>
                                  {lighting.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        <div className="input-row">
                          <div className="input-group">
                            <label htmlFor="camera-angle">Camera Angle</label>
                            <select
                              id="camera-angle"
                              value={cameraAngle}
                              onChange={(e) => setCameraAngle(e.target.value)}
                              disabled={loading}
                            >
                              {cameraAngleOptions.map((angle) => (
                                <option key={angle.value} value={angle.value}>
                                  {angle.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="input-group">
                            <label htmlFor="artistic-filter">Artistic Filter</label>
                            <select
                              id="artistic-filter"
                              value={artisticFilter}
                              onChange={(e) => setArtisticFilter(e.target.value)}
                              disabled={loading}
                            >
                              {artisticFilterOptions.map((filter) => (
                                <option key={filter.value} value={filter.value}>
                                  {filter.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                 
                  <button
                    className="generate-button"
                    onClick={() => {
                      const dynamicPrompt = buildDynamicPrompt();
                      handleGenerateImage(dynamicPrompt);
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Generate Image'}
                  </button>

                   {error && (
                     <div className="error-container">
                       <div className="error-icon">⚠️</div>
                       <div className="error-message">
                         {error}
                       </div>
                     </div>
                   )}
                 </div>
               )}
             </div>
           </div>
         </section>

         {/* Generated Images Gallery */}
         {generatedImages.length > 0 && (
           <section className="gallery-section">
             <div className="container">
               <div className="gallery-header">
                 <div className="gallery-title">
                   <h2 className="section-title text-left">Your Created Images ({generatedImages.length}/5)</h2>
                   <div className="storage-info">
                     <span className="storage-indicator">
                       {generatedImages.length === 5 ? (
                         <span className="full-storage">
                           <FaInfoCircle /> Storage full - oldest image will be replaced
                         </span>
                       ) : (
                         <span className="available-storage">
                           <FaInfoCircle /> {5 - generatedImages.length} slot{5 - generatedImages.length !== 1 ? 's' : ''} available
                         </span>
                       )}
                     </span>
                   </div>
                 </div>
                 <button
                   className="clear-all-button"
                   onClick={handleClearAllImages}
                   title="Clear all images"
                 >
                   Clear All
                 </button>
               </div>
               <div className="images-grid">
                 {generatedImages.map((image, index) => (
                   <div key={image.id} className={`image-card ${index === 4 ? 'oldest-image' : ''}`}>
                     <div className="image-position">
                       {index === 0 && <span className="position-badge newest">Newest</span>}
                       {index === 4 && <span className="position-badge oldest">Next to remove</span>}
                     </div>
                     <img
                       src={image.url}
                       alt={image.prompt}
                       onClick={() => handleOpenImageModal(image)}
                       style={{ cursor: 'pointer' }}
                     />
                     <div className="image-info">
                       <div className="image-actions">
                         <button
                           className="download-button"
                           onClick={() => handleDownloadImage(image.url, image.prompt)}
                         >
                           <FaDownload /> Download
                         </button>
                         <button
                           className="delete-button"
                           onClick={() => handleDeleteImage(image.id)}
                           title="Delete image"
                         >
                           ×
                         </button>
                       </div>
                     </div>
                   </div>
                 ))}
               </div>
             </div>
           </section>
         )}

         {/* Features Section */}
         <section className="features-section">
           <div className="container">
             <h2 className="section-title">Image Creator Features</h2>
             <div className="features-grid">
               <div className="feature-item">
                 <FaPalette />
                 <h3>AI Powered</h3>
                 <p>Advanced AI algorithms create unique images from your descriptions</p>
               </div>
               <div className="feature-item">
                 <FaWallet />
                 <h3>Wallet Integration</h3>
                 <p>Connect your Radix wallet to save and manage your creations</p>
               </div>
               <div className="feature-item">
                 <FaDownload />
                 <h3>Instant Download</h3>
                 <p>Download your generated images in high quality instantly</p>
               </div>
             </div>
           </div>
         </section>
       </main>

       <Footer />
       <TelegramPopup />
       
       {/* Image Modal */}
       {modalImage && (
         <div className="image-modal-overlay" onClick={handleCloseModal}>
           <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
             <button className="modal-close-button" onClick={handleCloseModal}>
               <FaTimes />
             </button>
             <img src={modalImage.url} alt={modalImage.prompt} />
           </div>
         </div>
       )}
     </div>
   );
 };

export default ImageCreator;
